import { Link } from "react-router-dom";
import Room from "@/components/Room";

export default function NatureAsset() {
  return (
    <Room title="Nature Asset" description="What changes when a living system is held as infrastructure that yields every year.">
      <div className="fade">
        <div className="kicker">Nature Asset · Metabolic Infrastructure</div>
        <h1 data-testid="nature-asset-h1">Capital cannot speed up botanical time.</h1>
        <div className="goldrule"></div>
        <p className="lede">Nature has been priced as a stock to extract or a credit to sell once. Held as infrastructure, a living system yields every year for as long as its clock allows, and the clock is where the value is decided.</p>
      </div>

      <section className="q">
        <div className="n">01</div>
        <h2>What is a coconut palm worth?</h2>
        <p>A single productive palm yields around 150 nuts a year, and each nut separates into water, meat, shell, husk and sap. Each has its own market: hydration, oil and flour, activated carbon and charcoal, fibre and growing media, sugar. Most of that biomass is still burned.</p>
        <p>A palm is therefore not one commodity. It is a ledger of revenue streams that move independently of one another. If one price falls, another can be pursued. What does the asset you hold produce that nobody is selling?</p>
      </section>

      <section className="q">
        <div className="n">02</div>
        <h2>What separates an asset from infrastructure?</h2>
        <p>An extractive asset is valued on what can be taken from it once: the timber, the mineral, the harvest. Infrastructure is valued on what it carries every year for as long as it stands.</p>
        <p>Metabolic infrastructure monetises the ongoing metabolism of a living system, its growth, cycles, regeneration and decay, rather than a single extraction. It is priced on the flow, and the flow is only as long as the biological clock beneath it.</p>
      </section>

      <section className="q">
        <div className="n">03</div>
        <h2>Why has nature never become an asset class?</h2>
        <p>Shares became an asset class once a certificate, a registry and a market existed to hold and transfer them. Nature has had none of the three: no standard unit, no custodial register and no route to liquidity. Without them a forest or a coastline remains something admired and never held. The missing piece is plumbing, not valuation.</p>
      </section>

      <section className="q">
        <div className="n">04</div>
        <h2>What would a unit of nature look like?</h2>
        <p>A carbon credit sells one service, once. The NEU is a different instrument: one hectare of living, productive system, held as a share in what it yields each year rather than a claim on a single service.</p>
        <ul className="plain">
          <li>A shorter term draws on existing stock and the value locked in its waste.</li>
          <li>A longer term draws on new planting and the decades of compounding that follow it.</li>
          <li>Part of the yield stays with the community that farms it, because the unit fails when they leave.</li>
        </ul>
      </section>

      <section className="q">
        <div className="n">05</div>
        <h2>What does the clock do to all of this?</h2>
        <p>A palm takes five to seven years to bear, and past its productive life it does not come back. No quantity of capital shortens the first or reverses the second. The value of a nature asset therefore depends on where it sits in biological time on the day it is bought, and the same hectare can be an asset in one decade and a liability in the next.</p>
        <p>Bought before the line, a hectare compounds. Bought after it, the same price pays for a liability. <Link to="/origin-risk" data-testid="nature-asset-origin-link">Where a living system stands in its time</Link> is the first question, not the last.</p>
      </section>

      <section className="close">
        <p className="ask">You hold land, lend against it, or buy what grows on it. Do you know which side of its line each hectare sits on, and how long before everyone else does?</p>
        <Link className="write" to="/enter/nature-asset" data-testid="nature-asset-write">Write</Link>
      </section>
    </Room>
  );
}
